import { Box, chakra, Flex, HStack, Stack, Table, Text } from '@chakra-ui/react';
import { ExternalLink, Info, Loader2 } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { getRelatedMarkets } from '../api/client';
import type { Quake, RelatedMarket } from '../api/types';
import { pctFromProb, pp } from '../lib/format';
import { polymarketEventUrlFromEventSlug } from '../lib/polymarket';
import { ScoreGauge } from './ScoreGauge';

export function RelatedMarkets({ leader, window }: { leader?: Quake; window: string }) {
  const [showAll, setShowAll] = useState(false);
  const marketID = leader?.market_id ?? '';
  const hasLeader = marketID.length > 0;
  const relatedQuery = useQuery({
    queryKey: ['related-markets', marketID, window],
    queryFn: ({ signal }) => getRelatedMarkets(marketID, window, 10, { signal }),
    enabled: hasLeader,
    refetchInterval: 5_000,
  });
  const items = relatedQuery.data?.items ?? [];
  const visibleItems = showAll ? items : items.slice(0, 5);
  const hasMore = items.length > visibleItems.length;

  return (
    <Box
      as="section"
      layerStyle="glassPanel"
      borderRadius="oqPanel"
      display="flex"
      flexDirection="column"
      minH={showAll ? '520px' : undefined}
      h={showAll ? undefined : '520px'}
      overflow={showAll ? 'visible' : 'hidden'}
    >
      <Flex
        flexShrink={0}
        align="center"
        justify="space-between"
        borderBottomWidth="1px"
        borderColor="border.subtle"
        px={5}
        py={3}
      >
        <HStack gap={3}>
          <Box display="grid" placeItems="center" h={6} w={6} borderRadius="oqControl" bg="brand.solid/80" fontSize="sm" fontWeight="bold" color="brand.fg">
            4
          </Box>
          <Text fontSize="lg" fontWeight="semibold" color="text.primary">
            Correlation / Related Markets
          </Text>
        </HStack>
        {relatedQuery.isFetching ? <Loader2 className="oq-spin" size={16} color="currentColor" /> : null}
      </Flex>

      <Box minH={0} flex="1" overflow={showAll ? 'visible' : 'hidden'}>
        {!hasLeader ? (
          <RelatedEmptyState
            title="No leader market selected."
            detail="Related-market inference appears after a live mover clears the filters."
          />
        ) : relatedQuery.isError ? (
          <RelatedEmptyState title="Related markets unavailable." detail="The live movers feed is still working." />
        ) : visibleItems.length === 0 ? (
          <RelatedEmptyState
            title="No related markets found yet."
            detail="Oddsquake is building links from event, category, tag, and text similarity."
          />
        ) : (
          <RelatedMarketsTable items={visibleItems} />
        )}
      </Box>

      <Flex
        flexShrink={0}
        direction={{ base: 'column', sm: 'row' }}
        align={{ sm: 'center' }}
        justify={{ sm: 'space-between' }}
        gap={2}
        borderTopWidth="1px"
        borderColor="border.subtle"
        px={5}
        py={3}
        fontSize="xs"
        color="text.muted"
      >
        <HStack gap={2}>
          <Box as="span" w="3.5" h="3.5" display="inline-block">
            <Info />
          </Box>
          <Text>Related markets are structural neighbors, not catalysts or news matches.</Text>
        </HStack>
        {hasMore || showAll ? (
          <chakra.button
            type="button"
            onClick={() => setShowAll((value) => !value)}
            textAlign={{ base: 'left', sm: 'right' }}
            fontWeight="semibold"
            color="brand.fg"
            transition="colors"
            _hover={{ color: 'text.primary' }}
          >
            {showAll ? 'Show fewer' : 'View related markets'}
          </chakra.button>
        ) : null}
      </Flex>
    </Box>
  );
}

function RelatedMarketsTable({ items }: { items: RelatedMarket[] }) {
  return (
    <Box className="scrollbar-soft" overflowX="auto" overflowY="hidden">
      <Table.Root size="sm" variant="line" minW="720px">
        <Table.Header>
          <Table.Row borderBottomWidth="1px" borderColor="border.subtle" textAlign="left" fontSize="xs" fontWeight="medium" color="text.muted">
            <Table.ColumnHeader px={4} py={3}>
              Market
            </Table.ColumnHeader>
            <Table.ColumnHeader px={4} py={3}>
              Reason
            </Table.ColumnHeader>
            <Table.ColumnHeader px={4} py={3}>
              Static
            </Table.ColumnHeader>
            <Table.ColumnHeader px={4} py={3}>
              Corr
            </Table.ColumnHeader>
            <Table.ColumnHeader px={4} py={3}>
              Move
            </Table.ColumnHeader>
            <Table.ColumnHeader px={4} py={3}>
              Price
            </Table.ColumnHeader>
            <Table.ColumnHeader px={4} py={3} textAlign="right">
              Quake
            </Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {items.map((market) => {
            const url = polymarketEventUrlFromEventSlug(market.event_slug);
            return (
              <Table.Row
                key={market.related_market_id}
                borderBottomWidth="1px"
                borderColor="border.subtle"
                _last={{ borderBottomWidth: 0 }}
                _hover={{ bg: 'surface.control.hovered' }}
              >
                <Table.Cell maxW="18rem" px={4} py={3}>
                  <Flex align="flex-start" gap={2}>
                    <Box minW={0}>
                      <Text fontWeight="medium" lineHeight="snug" color="text.primary" lineClamp={2}>
                        {market.question}
                      </Text>
                      <Text mt={1} fontSize="xs" color="text.muted">
                        {market.category || 'uncategorized'}
                      </Text>
                    </Box>
                    {url ? (
                      <chakra.a
                        href={url}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label="Open related market on Polymarket"
                        mt={0.5}
                        flexShrink={0}
                        color="text.muted"
                        transition="colors"
                        _hover={{ color: 'signal.blue' }}
                      >
                        <ExternalLink size={16} />
                      </chakra.a>
                    ) : null}
                  </Flex>
                </Table.Cell>
                <Table.Cell px={4} py={3}>
                  <Box
                    as="span"
                    title={market.reason}
                    display="inline-block"
                    maxW="8rem"
                    truncate
                    whiteSpace="nowrap"
                    borderRadius="oqControl"
                    borderWidth="1px"
                    borderColor="brand.solid/20"
                    bg="brand.subtle"
                    px={2.5}
                    py={1}
                    verticalAlign="middle"
                    fontSize="xs"
                    fontWeight="semibold"
                    color="brand.fg"
                  >
                    {market.reason}
                  </Box>
                </Table.Cell>
                <Table.Cell whiteSpace="nowrap" px={4} py={3} color="text.primary">
                  {(market.static_score * 100).toFixed(0)}%
                </Table.Cell>
                <Table.Cell whiteSpace="nowrap" px={4} py={3} color="text.muted">
                  {market.correlation == null ? 'static' : market.correlation.toFixed(2)}
                </Table.Cell>
                <Table.Cell
                  whiteSpace="nowrap"
                  px={4}
                  py={3}
                  fontWeight="bold"
                  color={market.move_pp >= 0 ? 'signal.good' : 'signal.bad'}
                >
                  {hasPrice(market) ? pp(market.move_pp) : '-'}
                </Table.Cell>
                <Table.Cell whiteSpace="nowrap" px={4} py={3} color="text.primary">
                  {hasPrice(market) ? `${pctFromProb(market.old_price)} -> ${pctFromProb(market.new_price)}` : '-'}
                </Table.Cell>
                <Table.Cell px={4} py={3}>
                  <Flex justify="flex-end">
                    {market.score > 0 ? <ScoreGauge score={market.score} small /> : <Text color="text.muted">-</Text>}
                  </Flex>
                </Table.Cell>
              </Table.Row>
            );
          })}
        </Table.Body>
      </Table.Root>
    </Box>
  );
}

function RelatedEmptyState({ title, detail }: { title: string; detail: string }) {
  return (
    <Box display="grid" minH="full" placeItems="center" px={5} py={8}>
      <Stack maxW="md" align="center" textAlign="center" gap={0}>
        <Flex
          display="grid"
          placeItems="center"
          h={12}
          w={12}
          mx="auto"
          borderRadius="oqPanel"
          borderWidth="1px"
          borderColor="border.subtle"
          bg="blackAlpha.200"
          color="brand.fg"
        >
          <Info size={24} />
        </Flex>
        <Text mt={5} fontSize="lg" fontWeight="semibold" color="text.primary">
          {title}
        </Text>
        <Text mt={2} fontSize="sm" lineHeight="6" color="text.muted">
          {detail}
        </Text>
      </Stack>
    </Box>
  );
}

function hasPrice(market: RelatedMarket) {
  return market.old_price > 0 || market.new_price > 0;
}